import React, { useState } from 'react';
import {Button, Group, Loader, Radio, ScrollArea, Stack, Text, Title} from '@mantine/core';
import SCModal from '@/PageComponents/Modal/SCModal';
import { LocationDTO } from '@/interfaces/api/models';

// Confirms deactivating a location. When the location is primary, the other active
// locations of the same type are passed in as candidates:
//  - none: the type is left without a primary
//  - one: it becomes the new primary
//  - several: the user picks which one becomes the new primary
type Props = {
  location: LocationDTO;
  locationTypeName: string;
  candidates: LocationDTO[];
  hasUnsavedChanges?: boolean;
  saving?: boolean;
  onConfirm: (newPrimary: LocationDTO | null) => void;
  onCancel: () => void;
};

const LocationDeactivateModal = ({ location, locationTypeName, candidates, hasUnsavedChanges, saving, onConfirm, onCancel }: Props) => {
  const needsNewPrimary = !!location.IsPrimary && candidates.length > 0;
  const mustChoose = !!location.IsPrimary && candidates.length > 1;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const newPrimary = !needsNewPrimary ? null : mustChoose ? candidates.find((x) => x.ID === selectedId) ?? null : candidates[0];
  const canConfirm = !saving && (!mustChoose || !!newPrimary);

  const describe = (x: LocationDTO) => [x.Description, x.LocationDisplay].filter(Boolean).join(' - ');

  return (
    <SCModal open={true} decor={'none'} onClose={onCancel} size={'md'}>
      <Stack gap={'md'} data-testid={'location-active-status-modal'}>
        <Title size={'md'} fw={600}>
          {mustChoose ? 'Select new primary location' : 'Deactivate location'}
        </Title>

        {!location.IsPrimary && <Text size={'sm'}>Are you sure you want to deactivate {location.Description}?</Text>}

        {location.IsPrimary && candidates.length === 0 && (
          <Text size={'sm'}>
            {location.Description} is the only active {locationTypeName} location. Once deactivated this customer will have no primary{' '}
            {locationTypeName} location.
          </Text>
        )}

        {location.IsPrimary && candidates.length === 1 && (
          <Text size={'sm'}>
            {location.Description} is the primary {locationTypeName} location. <b>{candidates[0].Description}</b> will become the primary{' '}
            {locationTypeName} location.
          </Text>
        )}

        {mustChoose && (
          <>
            <Text size={'sm'}>
              {location.Description} is the primary {locationTypeName} location. Select which location should become the primary{' '}
              {locationTypeName} location.
            </Text>
            <ScrollArea.Autosize mah={'clamp(200px, 50vh, 400px)'}>
              <Radio.Group value={selectedId} onChange={setSelectedId}>
                <Stack gap={'xs'}>
                  {candidates.map((x) => (
                      <Radio key={x.ID} value={x.ID as string} label={describe(x)} color={'scBlue'} />
                  ))}
                </Stack>
              </Radio.Group>
            </ScrollArea.Autosize>
          </>
        )}

        {hasUnsavedChanges && (
          <Text size={'sm'} c={'yellow.8'}>
            You have unsaved changes to this location. They will be discarded.
          </Text>
        )}

        <Group mt={'lg'} justify={'right'} gap={'xs'}>
          <Button type={'button'} variant={'subtle'} color={'gray.9'} onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            color={'red'}
            disabled={!canConfirm}
            rightSection={saving && <Loader variant={'oval'} size={18} color={'white'} />}
            onClick={() => onConfirm(newPrimary)}
          >
            Deactivate
          </Button>
        </Group>
      </Stack>
    </SCModal>
  );
};

export default LocationDeactivateModal;
